import { useEffect, useMemo, useState } from 'react'
import PlanWeekListHeader from './PlanWeekListHeader.jsx'
import { defaultPlannerCalendarContext } from '../../utils/mealPlanHelpers.js'
import SharedEmptyHero from '../StateBanner/EmptyHero.jsx'
import { WELCOME_SPLASH_EXPERIMENT_ID } from '../../config/welcomeSplashCopy.js'
import { trackUiEvent, trackWelcomeSplashShownOnce } from '../../utils/uiAnalytics.js'
import './planner.css'

const HERO_DISMISS_SESSION_KEY = 'agent_plan_activation_hero_dismissed_v1'

const GUEST_PREVIEW = {
  eyebrow: 'Plan',
  title: 'Your week of dinners lives here.',
  body: 'After you set up your kitchen, pick meals for the week and see what you can cook from your pantry.',
}

/**
 * @param {{
 *   welcomeCopy: import('../../config/welcomeSplashCopy.js').WELCOME_SPLASH_COPY['a'] | null,
 *   welcomeVariant: 'a' | 'b',
 *   onSetupKitchen: () => void,
 * }} props
 */
export default function PlannerGuestPreview({ welcomeCopy, welcomeVariant, onSetupKitchen }) {
  const calCtx = defaultPlannerCalendarContext()
  const emptyWeek = useMemo(() => ({}), [])
  const [heroDismissed, setHeroDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(HERO_DISMISS_SESSION_KEY) === '1'
    } catch {
      return false
    }
  })

  const showActivationHero = Boolean(welcomeCopy) && !heroDismissed

  useEffect(() => {
    if (!showActivationHero) return
    trackWelcomeSplashShownOnce({
      copy_variant: welcomeVariant,
      experiment_id: WELCOME_SPLASH_EXPERIMENT_ID,
      surface: 'plan_inline_hero',
    })
  }, [showActivationHero, welcomeVariant])

  function dismissHero() {
    try {
      sessionStorage.setItem(HERO_DISMISS_SESSION_KEY, '1')
    } catch {
      // ignore
    }
    setHeroDismissed(true)
    trackUiEvent('activation_hero_dismissed', {
      copy_variant: welcomeVariant,
      experiment_id: WELCOME_SPLASH_EXPERIMENT_ID,
    })
  }

  function handleSetupKitchen() {
    trackUiEvent('activation_hero_cta_clicked', {
      copy_variant: welcomeVariant,
      experiment_id: WELCOME_SPLASH_EXPERIMENT_ID,
      cta: welcomeCopy?.ctaPrimary || 'Set up my kitchen',
    })
    onSetupKitchen?.()
  }

  const activationHero = showActivationHero ? (
    <SharedEmptyHero
      className="planner-activation-hero"
      eyebrow={welcomeCopy.eyebrow}
      headline={welcomeCopy.title}
      body={
        <>
          {(welcomeCopy.lead || []).map((paragraph) => (
            <p key={paragraph} className="planner-activation-hero__lead">
              {paragraph}
            </p>
          ))}
          {welcomeCopy.trustLine ? (
            <p className="planner-activation-hero__trust">{welcomeCopy.trustLine}</p>
          ) : null}
        </>
      }
      actions={[
        {
          label: welcomeCopy.ctaPrimary || 'Set up my kitchen',
          onClick: handleSetupKitchen,
        },
        {
          label: welcomeCopy.ctaSecondary || 'Look around first',
          kind: 'secondary',
          onClick: dismissHero,
        },
      ]}
    />
  ) : null

  return (
    <>
      <div className="planner-panel-desktop">
        <div className="planner-page planner-page--guest">
          {activationHero}
          <div className="planner-hero" aria-hidden={showActivationHero ? 'true' : undefined}>
            <div className="eyebrow planner-eyebrow-tight">Plan</div>
            <div className="h-display planner-title-lg">Plan Week</div>
            <div className="planner-guest-sub">{GUEST_PREVIEW.body}</div>
          </div>

          <div className="planner-grid cold-start" aria-hidden="true">
            <div>
              <PlanWeekListHeader
                assignedByDay={emptyWeek}
                dayDates={calCtx.dayDates}
                todayKey={calCtx.todayKey}
                density="desktop"
                dimmed
              />
              <SharedEmptyHero
                className="planner-empty-state-hero"
                eyebrow={GUEST_PREVIEW.eyebrow}
                headline={GUEST_PREVIEW.title}
                body={GUEST_PREVIEW.body}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="planner-panel-mobile">
        <div className="mobile-planner mobile-planner--guest">
          {activationHero}
          <div className="planner-guest-section" aria-hidden={showActivationHero ? 'true' : undefined}>
            <div className="eyebrow planner-eyebrow-tight">Plan</div>
            <div className="planner-guest-title">Plan Week</div>
          </div>
          <div aria-hidden="true">
            <SharedEmptyHero
              className="mobile-planner-empty-state-hero"
              eyebrow={GUEST_PREVIEW.eyebrow}
              headline={GUEST_PREVIEW.title}
              body={GUEST_PREVIEW.body}
            />
          </div>
        </div>
      </div>
    </>
  )
}
